import { useState, useMemo } from "react";
import { formatTitle } from "@/lib/typography";
import { Glossaire } from "./Glossaire";

interface TreeNode {
  title: string;
  bullet?: string[];
  children?: TreeNode[];
}

const Tree = ({ node }: { node: TreeNode }) => {
  const [open, setOpen] = useState(true);
  const hasChildren = Array.isArray(node.children) && node.children.length > 0;

  return (
    <div className="ml-4">
      <div className="flex items-start gap-2">
        {hasChildren ? (
          <button
            onClick={() => setOpen((s) => !s)}
            className="mt-0.5 h-5 w-5 shrink-0 rounded border border-border bg-card text-xs leading-4 hover:bg-muted transition-colors"
            title={open ? "Réduire" : "Déployer"}
          >
            {open ? "−" : "+"}
          </button>
        ) : (
          <div className="mt-0.5 h-5 w-5" />
        )}
        <div className="pb-1">
          <div className="text-sm font-medium text-foreground">{node.title}</div>
          {node.bullet && (
            <ul className="mt-1 list-disc pl-5 text-xs text-muted-foreground space-y-0.5">
              {node.bullet.map((b, i) => (
                <li key={i}>{b}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {hasChildren && open && (
        <div className="mt-1 ml-5 border-l border-border pl-4">
          {node.children!.map((c, i) => (
            <Tree key={i} node={c} />
          ))}
        </div>
      )}
    </div>
  );
};

export const MindmapPage = () => {
  const structure = useMemo<TreeNode>(() => {
    return {
      title: "Amazon Ads — Panorama",
      children: [
        {
          title: "Pré-requis Retail Readiness",
          children: [
            {
              title: "Fiche produit",
              bullet: [
                "Images 7+ — vidéo",
                "Bullets orientés bénéfices",
                "A+ clair",
              ],
            },
            {
              title: "Offre & logistique",
              bullet: [
                "Prix cohérent",
                "Stock 30 jours",
                "Livraison fiable",
              ],
            },
          ],
        },
        {
          title: "Budgets & Règles",
          children: [
            { title: "Minimal", bullet: ["50 à 80 €/j — validation signal (14 j)"] },
            { title: "Recommandé", bullet: ["120 à 200 €/j — apprentissage rapide (7 à 14 j)"] },
            { title: "Optimal", bullet: ["250 à 500 €/j — agressif contrôlé (SoV)"] },
            { title: "Répartition", bullet: ["SP 60 à 75 % / SB 15 à 25 % / SD 10 à 15 %"] },
            { title: "Bornes quotidiennes", bullet: ["≥ 5 à 10× CPC moyen × clics visés/jour"] },
          ],
        },
        {
          title: "Phases & Cadence",
          bullet: [
            "Lancement — traction — consolidation — expansion — optimisation continue",
          ],
        },
        {
          title: "Scénarios — Marque inconnue",
          children: [
            {
              title: "Concurrence forte",
              bullet: [
                "SP Auto (4 sous-types)",
                "SP Manuel 10 à 20 KW (Exact/Phrase)",
                "SP ASIN — 20 à 40 concurrents",
                "SB Vidéo 3 à 5 KW cœur",
                "SD Views 7 à 30j + Product targeting PDP concurrents",
              ],
            },
            {
              title: "Concurrence modérée ou faible",
              bullet: [
                "SP Catégorie (+10 à 20 %)",
                "SB Vidéo sur catégorie",
                "SD Product targeting",
              ],
            },
            {
              title: "Blue Ocean",
              bullet: [
                "SP Broad modifié + découverte sémantique",
                "SB Vidéo éducative + Store storytelling",
                "SD audiences in-market adjacentes",
              ],
            },
          ],
        },
        {
          title: "Scénarios — Marque connue",
          children: [
            {
              title: "Défense",
              bullet: [
                "SP Exact marque + SKU clés",
                "SB Vidéo & Banner requêtes marque",
                "SD acheteurs 365j (cross/upsell)",
              ],
            },
            {
              title: "Conquête concurrentielle",
              bullet: [
                "SP ASIN concurrents",
                "SB Vidéo comparative (USP forts)",
                "SD Product targeting concurrents",
              ],
            },
          ],
        },
        {
          title: "Plans par Budget",
          children: [
            {
              title: "Minimal",
              bullet: [
                "SP Auto + Manuel Exact/Phrase (10 à 15 KW)",
                "SD Remarketing 30j",
                "SB Vidéo sur 3 à 5 KW (si éligible)",
              ],
            },
            {
              title: "Recommandé",
              bullet: [
                "+ SP ASIN targeting & Catégorie filtrée",
                "+ SB Banner & Vidéo (AB test)",
                "+ SD Product targeting & Views 7/30j",
              ],
            },
            {
              title: "Optimal",
              bullet: [
                "Doublons par segment d'intent",
                "Top-of-Search agressif",
                "Tests créa continus",
                "(Option) DSP",
              ],
            },
          ],
        },
      ],
    };
  }, []);

  const glossaire = [
    { term: "SP", definition: "Sponsored Products — publicité sur requêtes et produits" },
    { term: "SB", definition: "Sponsored Brands — publicité bannière et vidéo" },
    { term: "SD", definition: "Sponsored Display — retargeting et ciblage produit" },
    { term: "Exact", definition: "Correspondance exacte" },
    { term: "Phrase", definition: "Correspondance expression" },
    { term: "Broad", definition: "Correspondance large contrôlée" },
    { term: "Harvest", definition: "Transfert des requêtes gagnantes vers Manuel" },
    { term: "Pruning", definition: "Suppression des requêtes non performantes" },
    { term: "SoV", definition: "Share of Voice — part de voix" },
    { term: "NTB%", definition: "Pourcentage de nouveaux acheteurs" },
  ];

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold mb-3">{formatTitle("1) Mindmap — panorama (arborescence)")}</h2>
        <div className="rounded-lg border p-4 bg-card">
          <Tree node={structure} />
        </div>
      </div>

      <Glossaire entries={glossaire} />
    </div>
  );
};
